import {
  DesktopDropdownContent,
  DesktopDropdownItem,
  DesktopDropdownLabel,
} from '@crowdex/ui/components/desktop';
import { DropdownMenu, DropdownMenuTrigger } from '@crowdex/ui/components/dropdown-menu';
import type { WorkspacePort } from '@crowdex/workspace-protocol';
import {
  ChevronDown,
  Cloud,
  CloudUpload,
  FlaskConical,
  FolderGit2,
  GitFork,
  Hammer,
  Laptop,
  LoaderCircle,
  Network,
  Play,
  SquareArrowOutUpRight,
  Terminal,
} from 'lucide-react';
import { useState } from 'react';
import {
  SummaryActions,
  SummaryButtonRow,
  SummaryPill,
  SummarySection,
} from '@/components/desktop-summary/summary-primitives';
import { basename } from '@/lib/desktop-paths';
import type { NativeWorkspaceAction, NativeWorkspaceOpener } from '@/lib/native-desktop';

export type SummaryPlace = 'local' | 'worktree' | 'cloud';

const PLACE: Record<SummaryPlace, { label: string; Icon: typeof Cloud }> = {
  cloud: { label: 'Cloud sandbox', Icon: Cloud },
  worktree: { label: 'Worktree', Icon: FolderGit2 },
  local: { label: 'This computer', Icon: Laptop },
};

const ACTION_ICON = {
  play: Play,
  test: FlaskConical,
  build: Hammer,
  terminal: Terminal,
} as const satisfies Record<NativeWorkspaceAction['icon'], typeof Play>;

/** "2 servers": how many listen, and their ports ("5173 · 3000"). */
export function serversLabel(ports: readonly WorkspacePort[]): {
  count: string;
  ports: string;
} {
  const sorted = [...ports].sort((left, right) => left.port - right.port);
  return {
    count: `${sorted.length} ${sorted.length === 1 ? 'server' : 'servers'}`,
    ports: sorted.map((entry) => entry.port).join(' · '),
  };
}

/**
 * Moving the conversation elsewhere (the existing handoff): a cloud conversation comes to this
 * computer after its confirmation; one on this computer goes to Crowdex Cloud, or between its
 * checkout and a worktree of its own.
 */
export type SummaryHandoff = {
  /** Hands the conversation over to `destination` (the shell's `continueOn`). */
  onContinueOn: (destination: SummaryPlace) => void;
  /** A cloud conversation's "Continue on this computer": its confirmation (D48, mobile 45). */
  onContinueLocally: () => void;
  /** A phone: the cloud conversation goes to one of the user's computers. */
  phone?: boolean;
  /** A git checkout is read here: it can go to the cloud (its repository and branch). */
  canSendToCloud: boolean;
  /** The agent is working: nothing moves meanwhile. */
  disabled: boolean;
  /** A handoff is under way. */
  pending: boolean;
};

/** The workspace's actions (dev, test) and its editor, for a conversation on this computer. */
export type SummaryWorkspaceTools = {
  actions: readonly NativeWorkspaceAction[];
  onRunAction?: ((action: NativeWorkspaceAction) => void) | undefined;
  ide: NativeWorkspaceOpener | null;
  onOpenInIde?: ((openerId: string) => void) | undefined;
};

function WorkspaceActionRow({
  actions,
  onRunAction,
}: {
  actions: readonly NativeWorkspaceAction[];
  onRunAction: (action: NativeWorkspaceAction) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = actions.find((action) => action.id === selectedId) ?? actions[0];
  if (selected === undefined) return null;
  const Icon = ACTION_ICON[selected.icon];
  return (
    <div className="flex min-w-0 items-center gap-0.5">
      <div className="min-w-0 flex-1">
        <SummaryButtonRow
          icon={<Icon />}
          onClick={() => onRunAction(selected)}
          title={`Run ${selected.name} in the Terminal`}
        >
          {selected.name}
        </SummaryButtonRow>
      </div>
      {actions.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              aria-label="Choose an action to run"
              className="flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/55 hover:text-foreground"
              type="button"
            >
              <ChevronDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DesktopDropdownContent align="end" className="w-56">
            <DesktopDropdownLabel>Run</DesktopDropdownLabel>
            {actions.map((action) => {
              const ActionIcon = ACTION_ICON[action.icon];
              return (
                <DesktopDropdownItem
                  className="gap-2"
                  key={action.id}
                  onSelect={() => {
                    setSelectedId(action.id);
                    onRunAction(action);
                  }}
                >
                  <ActionIcon className="size-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{action.name}</span>
                </DesktopDropdownItem>
              );
            })}
          </DesktopDropdownContent>
        </DropdownMenu>
      )}
    </div>
  );
}

/** The handoff's pills: where the conversation can go from where it runs. */
function HandoffActions({ place, handoff }: { place: SummaryPlace; handoff: SummaryHandoff }) {
  if (handoff.pending)
    return (
      <SummaryButtonRow icon={<LoaderCircle className="animate-spin" />}>
        <span className="text-muted-foreground">Moving the conversation…</span>
      </SummaryButtonRow>
    );
  const disabled = handoff.disabled;
  const busyTitle = disabled ? 'Wait for the agent to finish' : undefined;
  if (place === 'cloud')
    return (
      <SummaryActions label="Handoff">
        <SummaryPill
          disabled={disabled}
          icon={<Laptop />}
          onClick={handoff.onContinueLocally}
          title={busyTitle ?? 'Bring the conversation and its changes to your computer'}
        >
          {handoff.phone === true ? 'Continue on a computer' : 'Continue on this computer'}
        </SummaryPill>
      </SummaryActions>
    );
  return (
    <SummaryActions label="Handoff">
      {handoff.canSendToCloud && (
        <SummaryPill
          disabled={disabled}
          icon={<CloudUpload />}
          onClick={() => handoff.onContinueOn('cloud')}
          title={busyTitle ?? 'Continue the conversation in a cloud sandbox'}
        >
          Send to Crowdex Cloud
        </SummaryPill>
      )}
      {place === 'local' ? (
        <SummaryPill
          disabled={disabled}
          icon={<GitFork />}
          onClick={() => handoff.onContinueOn('worktree')}
          title={busyTitle ?? 'Continue in a worktree of its own'}
        >
          Continue in a worktree
        </SummaryPill>
      ) : (
        <SummaryPill
          disabled={disabled}
          icon={<Laptop />}
          onClick={() => handoff.onContinueOn('local')}
          title={busyTitle ?? 'Continue in the repository’s own checkout'}
        >
          Continue in the main checkout
        </SummaryPill>
      )}
    </SummaryActions>
  );
}

/**
 * The Summary's Runs in (D5, mobile 36): where the conversation runs (the cloud sandbox, this
 * computer, a worktree) and whether it runs, the servers listening in it (they open the Ports
 * tool), on this computer its workspace's actions and its editor, then where it can go (the
 * handoff: Continue on this computer, Send to Crowdex Cloud).
 */
export function SummaryEnvironmentSection({
  place,
  running,
  ports,
  onOpenPorts,
  workspace = null,
  tools,
  handoff,
}: {
  place: SummaryPlace;
  /** The sandbox is up (a cloud conversation's). */
  running: boolean;
  ports: readonly WorkspacePort[];
  onOpenPorts: () => void;
  /** The folder it runs in (a worktree's path). */
  workspace?: string | null;
  tools?: SummaryWorkspaceTools | undefined;
  handoff?: SummaryHandoff | undefined;
}) {
  const { label, Icon } = PLACE[place];
  const servers = serversLabel(ports);
  const local = place !== 'cloud' && workspace !== null;
  const ide = local ? (tools?.ide ?? null) : null;
  const onOpenInIde = tools?.onOpenInIde;
  const onRunAction = tools?.onRunAction;
  return (
    <SummarySection card title="Runs in">
      <SummaryButtonRow
        icon={<Icon />}
        title={workspace ?? undefined}
        trailing={
          running ? (
            <span className="text-success">Running</span>
          ) : place === 'worktree' && workspace !== null ? (
            <span className="font-mono text-xs">{basename(workspace)}</span>
          ) : undefined
        }
      >
        {label}
      </SummaryButtonRow>
      {ports.length > 0 && (
        <SummaryButtonRow
          icon={<Network />}
          onClick={onOpenPorts}
          title="Open the Ports tool"
          trailing={<span className="font-mono text-xs">{servers.ports}</span>}
        >
          {servers.count}
        </SummaryButtonRow>
      )}
      {local && onRunAction !== undefined && (tools?.actions.length ?? 0) > 0 && (
        <WorkspaceActionRow actions={tools?.actions ?? []} onRunAction={onRunAction} />
      )}
      {ide !== null && onOpenInIde !== undefined && (
        <SummaryButtonRow
          icon={
            <img
              alt=""
              className="size-4 object-contain"
              height={16}
              src={`/app-icons/${ide.id}.png`}
              width={16}
            />
          }
          onClick={() => onOpenInIde(ide.id)}
          title={`Open the folder in ${ide.name}`}
          trailing={<SquareArrowOutUpRight className="size-3.5" />}
        >
          Open in {ide.name}
        </SummaryButtonRow>
      )}
      {handoff !== undefined && <HandoffActions handoff={handoff} place={place} />}
    </SummarySection>
  );
}
